import React, { useContext, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { AppHeader, AppHeaderDesktop } from "@/components";
import { useAppHeaderHeight } from "@/hooks";
import { UIContext } from "@/context";
import { cn } from "@/lib/utils";
import {
  LANG_STORAGE_KEY,
  basePathname,
  langPath,
  useLang,
  useTranslation,
} from "@/i18n";

const ORIGIN = "https://rocknitive.com";

export const LayoutApp: React.FC = () => {
  const { appHeaderHeight } = useAppHeaderHeight();
  const { isDeviceMobile }: any = useContext(UIContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const lang = useLang();
  const t = useTranslation();

  // Language links for the headers (Desktop shows all, mobile a subset)
  const links = t.header.nav.map((item) => ({
    name: item.name,
    path: `/#/${item.id}`,
  }));

  // Auto-route first-time visitors with a non-German browser language to /en.
  // An explicitly chosen language (stored on switch) always wins.
  useEffect(() => {
    if (pathname !== "/") return;
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(LANG_STORAGE_KEY);
    } catch {
      /* storage unavailable - fall back to browser language */
    }
    if (stored === "de") return;
    const browserLang = (
      navigator.languages?.[0] ||
      navigator.language ||
      ""
    ).toLowerCase();
    if (stored === "en" || (!stored && browserLang && !browserLang.startsWith("de"))) {
      navigate("/en", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = t.meta.title;
    document.documentElement.lang = lang;

    // Keep canonical, og:url and hreflang alternates in sync with the route
    const canonicalUrl = `${ORIGIN}${pathname === "/" ? "/" : pathname}`;
    const base = basePathname(pathname);
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute("href", canonicalUrl);
    document
      .querySelector('meta[property="og:url"]')
      ?.setAttribute("content", canonicalUrl);
    document
      .querySelector('link[rel="alternate"][hreflang="de"]')
      ?.setAttribute("href", `${ORIGIN}${langPath(base, "de")}`);
    document
      .querySelector('link[rel="alternate"][hreflang="en"]')
      ?.setAttribute("href", `${ORIGIN}${langPath(base, "en")}`);
    document
      .querySelector('link[rel="alternate"][hreflang="x-default"]')
      ?.setAttribute("href", `${ORIGIN}${langPath(base, "de")}`);
  }, [lang, t, pathname]);

  useEffect(() => {
    setTimeout(() => {
      document.getElementById('scrollToTopAnchor')?.scrollIntoView({ behavior: "smooth" });
    }, 500);
  }, [pathname]);

  return (
    <>
      <div className="relative flex h-dvh w-screen flex-col bg-background-primary">
        {/* Outlet and content should scroll within this container */}
        <div
          className="flex flex-1 flex-col overflow-y-auto"
          style={{ paddingTop: appHeaderHeight }}
        >
          {/* App Header (conditionally rendered & hidden via tailwind classes depending on device) */}
          <AppHeader
            links={links.slice(0, 2)}
            className={cn(!isDeviceMobile && "md:hidden")}
          />

          {/** App Header for Desktop (hidden via tailwind classes for mobile devicess) */}
          <AppHeaderDesktop
            links={links}
            className={cn(isDeviceMobile ? "hidden" : "max-md:hidden")}
          />

          <div id="scrollToTopAnchor" className="relative top-[-70px] left-0"/>
          <Outlet key={"outlet"} />
        </div>
      </div>
    </>
  );
};
